import { ChargeItem } from '@/class/model/mdm/chargeItem'
import { Message } from '@/utils/Message'
import lang from '@/lang'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import * as ChargeMeasure from '@/class/cargo/model/parameters/ChargeMeasure'
import ChargeUtils from '@/class/cargo/model/utils/ChargeUtils'
import { Charge } from '@/class/model/act/Charge'
import $store from '@/store'
import { Company } from '@/class/model/mdm/Company'
import * as PriceConstant from '@/class/cargo/model/parameters/PriceConstant'
import { cache } from '@/utils/cacheManager'
import { multiply } from '@/utils/commonUtil'
import { getObjectByCache } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'

export class PriceLclUtil {
  constructor() {
  }

  /**
   * 海运费
   */
  static DEFAULT_SCALE_CODE_SEA = 'OF';

  /**
   * 生成费用
   * @param quotation
   * @param job
   */
  static async generatingCharge(quotation, job, priceLcl = null) {
    const chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_SEA)
    if (chargeItem == null) {
      Message.warning(lang.t('Price.NotMatchChargeItem', [lang.t('Price.SeaFreight'), this.DEFAULT_SCALE_CODE_SEA]))
      return
    }
    if (!job.charges || job.charges.length <= 0) {
      job.charges = []
    }
    const quotationLclDetail = quotation.quotationLclDetail
    if (quotationLclDetail) {
      let charge = null
      // 应收
      charge = await this.convertQuotationLclDetailToCharge(quotation, quotationLclDetail, job, chargeItem, true, priceLcl)
      if (charge != null) {
        job.charges.push(charge)
      }
      charge = await this.convertQuotationLclDetailToCharge(quotation, quotationLclDetail, job, chargeItem, false, priceLcl)
      if (charge != null) {
        job.charges.push(charge)
      }
    }
  }

  /**
   * 生成费用
   * @param quotation
   * @param quotationLclDetail
   * @param job
   */
  static async generatingChargeLclFromInquiry(quotation, quotationLclDetail, job) {
    const chargeItem = getObjectByCache(basicCacheKeys.basic_data_ChargeItem, 'scaleCode', this.DEFAULT_SCALE_CODE_SEA)
    if (chargeItem == null) {
      Message.warning(lang.t('Price.NotMatchChargeItem', [lang.t('Price.SeaFreight'), this.DEFAULT_SCALE_CODE_SEA]))
      return
    }
    if (!job.charges || job.charges.length <= 0) {
      job.charges = []
    }
    let charge = null
    // 应付
    charge = await this.convertQuotationLclDetailToCharge(quotation, quotationLclDetail, job, chargeItem, false, { bookingAgent: quotationLclDetail.source || 0 })
    if (charge != null) {
      job.charges.push(charge)
    }
  }

  static async convertQuotationLclDetailToCharge(quotation, quotationLclDetail, job, chargeItem, receivable = true, priceLcl = null) {
    if (receivable && quotationLclDetail.weightPrice === 0.0 && quotationLclDetail.volumePrice === 0.0) return null
    if (receivable && quotationLclDetail.weightPrice === PriceConstant.NIL && quotationLclDetail.volumePrice === PriceConstant.NIL) return null
    if (!receivable && quotationLclDetail.weightCost === 0.0 && quotationLclDetail.volumeCost === 0.0) return null
    if (!receivable && quotationLclDetail.weightCost === PriceConstant.NIL && quotationLclDetail.volumeCost === PriceConstant.NIL) return null
    const charge = new Charge().fillDefaultValue().getData()
    charge.jobUuid = job.uuid
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    charge.inputByDep = job.opDepartment <= 0 ? $store.getters.currentDeptId : job.opDepartment
    charge.branchId = job.opOffice <= 0 ? $store.getters.currentOfficeId : job.opOffice
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.chargeItemId = chargeItem.itemId
    charge.currency = quotationLclDetail.currency
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, quotationLclDetail.currency, job, charge.branchId)
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    charge.receivable = receivable ? 1 : 0
    let sum1 = 0.0
    let sum2 = 0.0
    if (receivable) {
      charge.accountId = job.customerId
      quotationLclDetail.weightPrice = quotationLclDetail.weightPrice === PriceConstant.NIL ? 0.00 : Number(quotationLclDetail.weightPrice)
      quotationLclDetail.volumePrice = quotationLclDetail.volumePrice === PriceConstant.NIL ? 0.00 : Number(quotationLclDetail.volumePrice)
      sum1 = quotation.grossWeight / 1000 * quotationLclDetail.weightPrice
      sum2 = quotation.cubage * quotationLclDetail.volumePrice
    } else {
      charge.accountId = priceLcl ? priceLcl.bookingAgent : 0
      quotationLclDetail.weightCost = quotationLclDetail.weightCost === PriceConstant.NIL ? 0.00 : Number(quotationLclDetail.weightCost)
      quotationLclDetail.volumeCost = quotationLclDetail.volumeCost === PriceConstant.NIL ? 0.00 : Number(quotationLclDetail.volumeCost)
      sum1 = quotation.grossWeight / 1000 * quotationLclDetail.weightCost
      sum2 = quotation.cubage * quotationLclDetail.volumeCost
    }
    if (sum1 > sum2) { // 单位为重量价
      charge.measure = ChargeMeasure.WGT_CHARGE_MEASURE.code
      charge.quantity = quotation.grossWeight / 1000
      charge.unitRate = receivable ? quotationLclDetail.weightPrice : quotationLclDetail.weightCost
    } else { // 单位为体积价
      charge.measure = ChargeMeasure.VOL_CHARGE_MEASURE.code
      charge.quantity = quotation.cubage
      charge.unitRate = receivable ? quotationLclDetail.volumePrice : quotationLclDetail.volumeCost
    }
    if (charge.quantity < 1) {
      charge.quantity = 1
    }
    // 单价是否含税
    charge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (charge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(charge, multiply(charge.unitRate, charge.quantity), 0)
    } else {
      ChargeUtils.calculateTax(charge, multiply(charge.unitRate, charge.quantity), 0)
    }
    charge.createdBy = quotationLclDetail.prcCreatedBy // 运价创建人
    return charge
  }
  /**
   * 其他费用
   */
  static async getQuotationChargeDetail(quotation, job, isReceivable, priceLcl = null) {
    if (quotation.quotationChargeDetail && quotation.quotationChargeDetail.length > 0) {
      let charge = null
      for (const chargeDetail of quotation.quotationChargeDetail) {
        if (chargeDetail.num > 0) {
          charge = new Charge().fillDefaultValue().getData()
          charge.chargeItemId = chargeDetail.chargeId
          charge.currency = chargeDetail.currency
          charge.measure = chargeDetail.unit
          charge.quantity = chargeDetail.num
          charge.unitRate = isReceivable ? chargeDetail.rate : chargeDetail.costRate
          charge.remarks = chargeDetail.remarks
          await this.getNewCharge(charge, job, isReceivable, priceLcl)
        }
      }
    }
  }

  static async getNewCharge(charge, job, isReceivable, priceLcl = null) {
    charge.jobUuid = job.uuid
    charge.businessDate = job.businessDate
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    const department = $store.getters.currentDept
    const office = $store.getters.currentOffice
    charge.inputByDep = !department ? 0 : department.departmentId
    charge.branchId = !office ? 0 : office.officeId
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, function(rate) {
      charge.exchangeRate = rate
    })
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    // 单价是否含税
    charge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (charge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    } else {
      ChargeUtils.calculateTax(charge, multiply(charge.unitRate, charge.quantity), charge.taxRate)
    }
    charge.verifiedAmount = 0
    charge.receivable = isReceivable
    if (!isReceivable) {
      charge.accountId = priceLcl ? priceLcl.bookingAgent : 0
    } else {
      charge.accountId = job.customerId
    }
    if (charge.accountId && charge.accountId > 0) {
      const company = await Company.getCompanyById(charge.accountId)
      if (company) {
        charge.oversea = (company.designationAgent || company.overseaAgent) // 指定货代理/目的港代理
      }
    }
    if (!job.charges || job.charges.length <= 0) job.charges = []
    job.charges.push(charge)
  }
}
